import { useRef, useState, type FormEvent } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { errorMessage, MOCK_ENABLED, useEnableTotp, useLogin, useMe } from "@/api";
import { useDocumentTitle } from "@/components/useDocumentTitle";
import { Button, Card, Input, Mono } from "@/ui";
import "./LoginPage.css";

/**
 * Two-step sign in: password, then TOTP. A not-yet-enrolled admin (C2: login returns
 * `totp_setup_uri` and a limited session) gets a "setup" step that calls /auth/totp/enable.
 */
type Step = "password" | "totp" | "setup";

const STEP_TEXT: Record<Step, string> = {
  password: "Sign in to the admin panel.",
  totp: "Enter the 6-digit code from your authenticator app.",
  setup: "Set up two-factor authentication to finish signing in.",
};

export function LoginPage() {
  useDocumentTitle("Sign in");
  const me = useMe();
  const login = useLogin();
  const enable = useEnableTotp();
  const navigate = useNavigate();
  const location = useLocation();
  const next = new URLSearchParams(location.search).get("next") || "/";

  const [stepState, setStep] = useState<Step>("password");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [setupUri, setSetupUri] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const codeRef = useRef<HTMLInputElement>(null);

  // A limited (setup-stage) session survives reloads: resume at the setup step.
  const pendingSetup = me.data?.totp_setup_required === true;
  const step: Step = pendingSetup ? "setup" : stepState;
  const uri = setupUri ?? me.data?.totp_setup_uri ?? null;

  if (me.data && !pendingSetup) return <Navigate to={next} replace />;

  const focusCode = () => requestAnimationFrame(() => codeRef.current?.focus());

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      if (step === "setup") {
        await enable.mutateAsync(code.trim());
        navigate(next, { replace: true });
        return;
      }
      const res = await login.mutateAsync({
        username: username.trim(),
        password,
        totp: step === "totp" ? code.trim() : undefined,
      });
      if (res.totp_required) {
        setStep("totp");
        focusCode();
      } else if (res.totp_setup_uri) {
        setSetupUri(res.totp_setup_uri);
        setStep("setup");
        setCode("");
        focusCode();
      } else if (res.admin) {
        navigate(next, { replace: true });
      }
    } catch (err) {
      setError(errorMessage(err));
      if (step !== "password") setCode("");
    }
  };

  const back = () => {
    setStep("password");
    setCode("");
    setError(null);
  };

  const busy = login.isPending || enable.isPending;
  const codeValid = /^\d{6}$/.test(code.trim());

  return (
    <div className="login">
      <Card padding="md" className="login__card">
        <form onSubmit={submit} className="stack" noValidate>
          <div>
            <h1 className="login__title">Relay</h1>
            <p className="muted login__sub">{STEP_TEXT[step]}</p>
          </div>

          {step === "password" ? (
            <>
              <Input
                label="Username"
                autoComplete="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoFocus
                required
              />
              <Input
                label="Password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </>
          ) : (
            <>
              {step === "setup" && uri ? (
                <div className="stack text-sm">
                  <span>Add this key to your authenticator app, then enter the code it shows:</span>
                  <Mono value={uri} />
                </div>
              ) : null}
              <Input
                ref={codeRef}
                label="Authentication code"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]{6}"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                required
              />
            </>
          )}

          {error ? (
            <div className="banner" role="alert">
              {error}
            </div>
          ) : null}

          <Button
            type="submit"
            variant="primary"
            fullWidth
            loading={busy}
            disabled={step === "password" ? !username.trim() || !password : !codeValid}
          >
            {step === "password" ? "Continue" : step === "setup" ? "Enable and sign in" : "Verify and sign in"}
          </Button>
          {step === "totp" ? (
            <Button type="button" variant="ghost" onClick={back}>
              Use a different account
            </Button>
          ) : null}
          {MOCK_ENABLED ? (
            <p className="faint">Mock mode: any username and password, then any 6-digit code.</p>
          ) : null}
        </form>
      </Card>
    </div>
  );
}
